/**
 * Tradução da forma crua da API (inglês) para o domínio pt-BR da tela
 * (`TP-049`, T9). Roda UMA vez, logo depois do fetch — ver `api/tipos.ts`.
 */

import type {
  Contrato,
  ContratoApi,
  Entidade,
  EntidadeDoContratoApi,
  EstadoDaFonte,
  EstadoDaFonteApi,
  FonteDetalhe,
  FonteDetalheApi,
  PendenciaDaFonte,
  PendenciaDaFonteApi,
  FonteResumo,
  FonteResumoApi,
  ItemApi,
  ItemDaComparacao,
  ItemDaComparacaoApi,
  ItemDoContrato,
  MotivoDaDivergencia,
  MotivoDaDivergenciaApi,
  PaginaDeComparacao,
  PaginaDeComparacaoApi,
  PaginaDeItens,
  PaginaDeItensApi,
  TipoDeFonte,
  TipoDeFonteApi,
  ConferenciaDoItem,
} from './tipos';
import { mapearSituacao } from './situacaoDaFonte';

const ENTIDADE: Record<EntidadeDoContratoApi, Entidade> = {
  HealthCare: 'saude',
  PlanOperator: 'operadora_de_planos',
};

export const TIPO_DE_FONTE: Record<TipoDeFonteApi, TipoDeFonte> = {
  ContractBase: 'contrato_base',
  Amendment: 'aditivo',
  Readjustment: 'reajuste',
  ExternalTable: 'tabela_externa',
  Operational: 'operacional',
};

/** Inverso de `TIPO_DE_FONTE` — o envio (`POST`) manda o NOME do membro em inglês. */
export const TIPO_DE_FONTE_PARA_API: Record<TipoDeFonte, TipoDeFonteApi> = {
  contrato_base: 'ContractBase',
  aditivo: 'Amendment',
  reajuste: 'Readjustment',
  tabela_externa: 'ExternalTable',
  operacional: 'Operational',
};

const ESTADO_DA_FONTE: Record<EstadoDaFonteApi, EstadoDaFonte> = {
  Received: 'recebida',
  TextExtracted: 'texto_extraido',
  Mapped: 'mapeada',
  RulesExtracted: 'regras_extraidas',
  UnderReview: 'em_revisao',
  Active: 'ativa',
  Rejected: 'rejeitada',
};

const MOTIVO_DA_DIVERGENCIA: Record<MotivoDaDivergenciaApi, MotivoDaDivergencia> = {
  DifferentValue: 'valor_diferente',
  DifferentUnit: 'unidade_diferente',
  OnlyInA: 'so_no_primeiro',
  OnlyInB: 'so_no_segundo',
};

/** Contrato sem nome (`name: null`) ganha um rótulo visível — nunca célula vazia numa lista de escolha. */
export function mapearContrato(bruto: ContratoApi): Contrato {
  return {
    id: bruto.id,
    nome: bruto.name ?? `Contrato ${bruto.id.slice(0, 8)}`,
    uf: bruto.uf,
    entidade: ENTIDADE[bruto.entity],
    ligadoAoIw: bruto.linkedToIw,
  };
}

/** Inverso de `ENTIDADE` — o cadastro (`POST /contratos`) manda o NOME do membro em inglês. */
export const ENTIDADE_PARA_API: Record<Entidade, EntidadeDoContratoApi> = {
  saude: 'HealthCare',
  operadora_de_planos: 'PlanOperator',
};

/**
 * Exportado para `features/contratos/revisao` (`TP-050`): `ativar`/`reprocessar`
 * devolvem o MESMO vocabulário de estado (`SourceIngestionState`) que esta
 * tradução já cobre — reaproveitar evita uma segunda tabela divergente.
 */
export function mapearEstadoDaFonte(bruto: EstadoDaFonteApi): EstadoDaFonte {
  return ESTADO_DA_FONTE[bruto];
}

export function mapearFonteResumo(bruto: FonteResumoApi): FonteResumo {
  return {
    id: bruto.id,
    tipo: TIPO_DE_FONTE[bruto.type],
    estado: ESTADO_DA_FONTE[bruto.state],
    vigenciaInicio: bruto.validFrom,
    vigenciaFim: bruto.validTo,
    criadaEm: bruto.createdAt,
  };
}

export function mapearPendencia(bruto: PendenciaDaFonteApi): PendenciaDaFonte {
  return { codigo: bruto.code, pagina: bruto.page, detalhe: bruto.detail, resolvida: bruto.resolved };
}

export function mapearFonteDetalhe(bruto: FonteDetalheApi): FonteDetalhe {
  return {
    id: bruto.id,
    contratoId: bruto.contractId,
    tipo: TIPO_DE_FONTE[bruto.type],
    estado: ESTADO_DA_FONTE[bruto.state],
    motivoDaRecusa: bruto.rejectionReason,
    paginas: bruto.pages,
    totalDeItens: bruto.itemCount,
    totalDePendencias: bruto.pendencyCount,
    criadaEm: bruto.createdAt,
    atualizadaEm: bruto.updatedAt,
    situacao: bruto.situation === undefined || bruto.situation === null ? null : mapearSituacao(bruto.situation),
  };
}

export function mapearItem(bruto: ItemApi): ItemDoContrato {
  return {
    id: bruto.id,
    codigo: bruto.code ?? '—',
    descricao: bruto.description ?? '—',
    grupo: bruto.group,
    unidadeDeCobranca: bruto.billingUnit ?? '—',
    valor: bruto.value,
    pagina: bruto.page,
    vigenciaInicio: bruto.validFrom,
    vigenciaFim: bruto.validTo,
    conferencia: conferenciaDoItem(bruto),
  };
}

/** Sem o campo (fixture antiga, API anterior ao `TP-067`), o item conta como lido com segurança. */
function conferenciaDoItem(bruto: ItemApi): ConferenciaDoItem {
  if (bruto.unitConfidence === undefined || bruto.unitConfidence === 'High') return 'segura';
  return bruto.reviewedAt !== null && bruto.reviewedAt !== undefined ? 'conferido' : 'nao_conferido';
}

export function mapearPaginaDeItens(bruto: PaginaDeItensApi): PaginaDeItens {
  return {
    itens: (bruto.items ?? []).map(mapearItem),
    total: bruto.total,
    pagina: bruto.page,
    tamanho: bruto.pageSize,
  };
}

export function mapearItemDaComparacao(bruto: ItemDaComparacaoApi): ItemDaComparacao {
  return {
    codigo: bruto.code ?? '—',
    itemA: bruto.itemA === null ? null : mapearItem(bruto.itemA),
    itemB: bruto.itemB === null ? null : mapearItem(bruto.itemB),
    motivo: MOTIVO_DA_DIVERGENCIA[bruto.reason],
  };
}

export function mapearPaginaDeComparacao(bruto: PaginaDeComparacaoApi): PaginaDeComparacao {
  return {
    divergencias: (bruto.items ?? []).map(mapearItemDaComparacao),
    total: bruto.total,
    pagina: bruto.page,
    tamanho: bruto.pageSize,
  };
}
